import 'bootstrap/dist/css/bootstrap.min.css';
import { useEffect, useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation
} from "react-router-dom";
import { useSelector } from "react-redux";
import './assets/css/style.css';

import Home from './View/Home';
import Login from "./View/Login";
import ViewSummary from './View/ViewSummary';
import Dashboard from './View/Dashboard';
import Resupply from './View/Resupply'
import Redirection from './View/Redirection';
import ResupplyDetails from "./View/ResupplyDetails"
import ResupplySummary from "./View/ResupplySummary"

function App() {
  const location = useLocation();
  const { auth } = useSelector((state) => state);

  const [isLoggedIn, setIsLoggedIn] = useState(
    Boolean(localStorage.getItem("isLoggedIn"))
  );

  /* ------------------------------------
     1️⃣ Capture tokens from URL - FIXED for SSO
  ------------------------------------- */
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");

    if (accessToken) {
      localStorage.setItem("access_token", accessToken);

      // When we have an access token, we should ensure the login state is true
      // But we need to let the Login page handle the actual authentication
      // So we don't set isLoggedIn here yet
    }

    if (refreshToken) {
      localStorage.setItem("refresh_token", refreshToken);
    }
  }, [location.search]);

  /* ------------------------------------
     2️⃣ Sync login state
  ------------------------------------- */
  useEffect(() => {
    const loggedIn = Boolean(localStorage.getItem("isLoggedIn"));
    setIsLoggedIn(loggedIn);
  }, [auth.isAuthenticated, location.pathname]);

  /* ------------------------------------
     3️⃣ Check for SSO callback on mount and route changes
  ------------------------------------- */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const accessToken = params.get("access_token");
    // If we have an access token in URL but we're not on login page,
    // we should redirect to login to process the token
    if (accessToken && !location.pathname.includes('/login')) {
      console.log("App.js: SSO callback detected, should redirect to login to process token");
      // Don't redirect here - let the routes handle it
    }
  }, [location.pathname, location.search, isLoggedIn]);

  /* ------------------------------------
     4️⃣ Preserve query params - IMPORTANT for SSO
  ------------------------------------- */
  const preserveQuery = location.search || "";

  /* ------------------------------------
     5️⃣ Login Redirect (preserve deal + country)
  ------------------------------------- */
  function LoginRedirect() {
    const { deal_id, countryId } = useParams();

    // IMPORTANT: Preserve ALL query parameters for SSO callback
    const currentSearch = location.search;

    if (deal_id && countryId) {
      return (
        <Navigate
          to={`/login/${deal_id}/${countryId}${currentSearch}`}
          replace
        />
      );
    } else {
      return <Navigate to={`/login${currentSearch}`} replace />;
    }
  }

  /* ------------------------------------
     6️⃣ Generic Redirect (preserve deal + country)
  ------------------------------------- */
  function RedirectWithDealAndCountry({ to }) {
    const parts = location.pathname.split("/");

    const deal_id = parts[2];
    const countryId = parts[3];

    // IMPORTANT: Preserve ALL query parameters
    const currentSearch = location.search;

    // If we have deal_id and countryId in the URL, preserve them
    if (deal_id && countryId) {
      return (
        <Navigate
          to={`/${to}/${deal_id}/${countryId}${currentSearch}`}
          replace
        />
      );
    } else {
      // If we don't have parameters, redirect to root of the target route
      // BUT preserve query params for SSO
      if (to === "dashboard") {
        return <Navigate to={`/dashboard${currentSearch}`} replace />;
      } else {
        return <Navigate to={`/${to}${currentSearch}`} replace />;
      }
    }
  }

  /* ------------------------------------
     7️⃣ Root Path Redirect Component
  ------------------------------------- */
  function RootRedirect() {
    // IMPORTANT: Preserve query params for SSO
    const currentSearch = location.search;

    if (isLoggedIn) {
      return <Navigate to={`/dashboard${currentSearch}`} replace />;
    } else {
      // When not logged in and we have access_token in URL,
      // we should go to login to process it
      return <Navigate to={`/login${currentSearch}`} replace />;
    }
  }

  /* ------------------------------------
     8️⃣ Dashboard Redirect Component for SSO processing
  ------------------------------------- */
  function DashboardRedirectWithQuery() {
    const currentSearch = location.search;
    return <Navigate to={`/dashboard${currentSearch}`} replace />;
  }

  /* ------------------------------------
     9️⃣ Check if current URL has access_token for SSO
  ------------------------------------- */
  const hasAccessTokenInUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.has("access_token");
  };

  /* ------------------------------------
     🔟 Routes - UPDATED for SSO
  ------------------------------------- */
  return (
    <Routes>
      {/* CRITICAL: Always allow access to login page, even when logged in,
          so SSO callbacks can be processed */}
      <Route path="/login" element={<Login />} />
      <Route path="/login/:deal_id/:countryId" element={<Login />} />

      {isLoggedIn ? (
        // {true ? ( requestType
        <>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/resupply" element={<Resupply />} />
          <Route path="/resupply/:hs_object_id" element={<Redirection />} />
          <Route path="/home/:deal_id/:countryId" element={<Home />} />
          <Route path="/home/:deal_id/:requestId/:hs_deal_id/:countryId" element={<ResupplyDetails />} />

          <Route path="/summary/:deal_id/:job_id/:country" element={<ViewSummary />} />
          <Route path="/summary/:deal_id/:job_id/:request_id/:country" element={<ResupplySummary />} />


          {/* If already logged in and trying to access login, redirect to dashboard */}
          <Route path="/login" element={<Navigate to="/dashboard" replace />} />
          <Route path="/login/:deal_id/:countryId" element={<Navigate to="/dashboard" replace />} />

          {/* Catch-all redirect */}
          <Route
            path="*"
            element={<RedirectWithDealAndCountry to="home" />}
          />
        </>
      ) : (
        <>
          {/* When NOT logged in */}
          <Route path="/" element={<RootRedirect />} />

          {/* Allow login routes to handle SSO */}
          <Route path="/login" element={<Login />} />
          <Route path="/login/:deal_id/:countryId" element={<Login />} />

          {/* Redirect all protected routes to login */}
          <Route path="/home/:deal_id/:countryId" element={<LoginRedirect />} />
          <Route path="/dashboard" element={<Navigate to="/login" replace />} />
          <Route path="/summary/:deal_id/:job_id/:country" element={<Navigate to="/login" replace />} />

          {/* Catch-all redirect to login */}
          <Route
            path="*"
            element={<RedirectWithDealAndCountry to="login" />}
          />
        </>
      )}
    </Routes>
  );
}

export default App;